import styled from '@emotion/styled'
import {
    ApiCollectionSectionItem,
    ApiContentSection,
    ApiProductGridLayout,
    ApiSectionType,
    buildCroppedImageUrl,
    DEFAULT_SQUARE_ASPECT_RATIO,
    getAppliedCropAspectRatio,
    hasAppliedImageCrop,
} from '@storefronts/common-types'
import React, { useCallback, useContext, useRef, useState } from 'react'
import { getCollectionProducts } from '../../../../api'
import {
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
    SUBTLE_PRIMARY_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { PageContext } from '../../../context/PageContextProvider'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import { getTypographyRoleStyle } from '../../../style/typography'
import { TYPOGRAPHY_SIZE } from '../../../style/typographySizes'
import { ButtonLoadingSpinner } from '../../icons/Icons'
import {
    ProductScrollContainer,
    ProductScrollItem,
    SectionContainer,
    SectionTitle,
} from './Shared'
import { scaledFontSize } from '../../../style/scrollingLayout'

const COLLECTIONS_GRID_COLUMN_COUNT = 2

const tileButtonStyles = {
    width: '100%',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left' as const,
    borderRadius: 8,
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    transition: 'opacity 0.15s ease',
    boxSizing: 'border-box' as const,
    minWidth: 0,

    '&:hover:not(:disabled)': {
        opacity: 0.85,
    },
    '&:active:not(:disabled)': {
        opacity: 0.75,
    },
    '&:focus-visible': {
        outline: `2px solid var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
        outlineOffset: 2,
    },
    '&:disabled': {
        cursor: 'not-allowed',
        opacity: 0.6,
    },
}

const CollectionsGridContainer = styled.div({
    display: 'grid',
    gridTemplateColumns: `repeat(${COLLECTIONS_GRID_COLUMN_COUNT}, minmax(0, 1fr))`,
    gridAutoRows: 'auto',
    width: '100%',
    gap: '16px 12px',
    boxSizing: 'border-box',
})

const CollectionGridTile = styled.button<{ hasText: boolean }>(
    ({ hasText }) => ({
        ...tileButtonStyles,
        gridRow: hasText ? 'span 2' : 'span 1',
        display: 'grid',
        gridTemplateRows: 'subgrid',
        rowGap: 8,
    }),
)

// Horizontal collection tiles span the full height of their ProductScrollItem
// cell and self-manage layout with flex. Not aligned as a subgrid across
// sibling tiles (unlike product cards) — collection alignment is out of scope
// for sc-35854.
const CollectionHorizontalTileButton = styled.button({
    ...tileButtonStyles,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    height: '100%',
})

const GridTileImageContainer = styled.div({
    gridRow: 1,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: '100%',
    minWidth: 0,
    minHeight: 0,
})

const HorizontalTileImageContainer = styled.div({
    flex: '1 1 auto',
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: '100%',
    minWidth: 0,
    minHeight: 0,
})

const TileTextContainer = styled.div({
    gridRow: 2,
    alignSelf: 'start',
    flex: '0 1 auto',
    minWidth: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
})

const HorizontalTileTextContainer = styled(TileTextContainer)({
    marginTop: 8,
})

const TileImage = styled.img<{ aspectRatio: string; isCropped: boolean }>(
    ({ aspectRatio, isCropped }) => ({
        width: '100%',
        aspectRatio,
        borderRadius: 8,
        display: 'block',
        ...(!isCropped && { objectFit: 'cover' }),
    }),
)

const TilePlaceholder = styled.div<{ aspectRatio: string }>(
    ({ aspectRatio }) => ({
        width: '100%',
        aspectRatio,
        background: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
        borderRadius: 8,
    }),
)

const TileTitle = styled.p({
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ...getTypographyRoleStyle('productTitle', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: 600,
        lineHeight: 1.35,
    }),
    fontSize: scaledFontSize(TYPOGRAPHY_SIZE.BODY),
})

const TileDescription = styled.p({
    margin: 0,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    ...getTypographyRoleStyle('bodyText', {
        lineHeight: 1.35,
    }),
    fontSize: scaledFontSize(12),
    opacity: 0.65,
})

const LoadingOverlay = styled.div({
    position: 'absolute',
    inset: 0,
    background: `color-mix(in srgb, var(${PRIMARY_PAGE_FILL_COLOR_VAR}) 60%, transparent)`,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
})

const CollectionTile: React.FC<{
    item: ApiCollectionSectionItem
    isHorizontal: boolean
    isLoading: boolean
    isDisabled: boolean
    onClick: () => void
}> = ({ item, isHorizontal, isLoading, isDisabled, onClick }) => {
    const title = item.collectionTitle?.trim()
    const description = item.description?.trim()
    const hasText = !!(title || description)

    const imageSrc = item.imageUrl
        ? buildCroppedImageUrl(item.imageUrl, item.imageCrop)
        : null
    const isCropped = item.imageUrl
        ? hasAppliedImageCrop(item.imageUrl, item.imageCrop)
        : false
    const imageAspectRatio = item.imageUrl
        ? getAppliedCropAspectRatio(item.imageUrl, item.imageCrop)
        : DEFAULT_SQUARE_ASPECT_RATIO

    const ImageContainer = isHorizontal
        ? HorizontalTileImageContainer
        : GridTileImageContainer
    const TextContainer = isHorizontal
        ? HorizontalTileTextContainer
        : TileTextContainer

    const content = (
        <>
            <ImageContainer>
                {imageSrc ? (
                    <TileImage
                        src={imageSrc}
                        alt={title ?? ''}
                        loading="lazy"
                        aspectRatio={imageAspectRatio}
                        isCropped={isCropped}
                    />
                ) : (
                    <TilePlaceholder aspectRatio={imageAspectRatio} />
                )}
                {isLoading && (
                    <LoadingOverlay>
                        <ButtonLoadingSpinner
                            color={`var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`}
                        />
                    </LoadingOverlay>
                )}
            </ImageContainer>
            {hasText && (
                <TextContainer>
                    {title && <TileTitle>{title}</TileTitle>}
                    {description && (
                        <TileDescription>{description}</TileDescription>
                    )}
                </TextContainer>
            )}
        </>
    )

    const tileProps = {
        onClick,
        disabled: isDisabled,
        'aria-label': title || description || 'Collection',
        type: 'button' as const,
    }

    if (isHorizontal) {
        return (
            <CollectionHorizontalTileButton {...tileProps}>
                {content}
            </CollectionHorizontalTileButton>
        )
    }

    return (
        <CollectionGridTile hasText={hasText} {...tileProps}>
            {content}
        </CollectionGridTile>
    )
}

export const CollectionsSection: React.FC<{
    section: Extract<
        ApiContentSection,
        { type: ApiSectionType.COLLECTIONS_SECTION }
    >
}> = ({ section }) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { openCollectionGrid } = useContext(PageContext)
    const [loadingCollectionId, setLoadingCollectionId] = useState<
        string | null
    >(null)

    // Tracks the latest click so a slow in-flight fetch can't open a stale PLP.
    const activeRequestRef = useRef(0)

    const handleTileClick = useCallback(
        async (item: ApiCollectionSectionItem) => {
            if (!item.collectionId || loadingCollectionId) return

            const requestId = ++activeRequestRef.current
            setLoadingCollectionId(item.collectionId)
            try {
                const links = await getCollectionProducts(
                    landingPage.landingPageHash,
                    item.collectionId,
                )
                // Ignore if a newer click superseded this request.
                if (activeRequestRef.current !== requestId) return
                openCollectionGrid(links)
            } catch (err) {
                console.error('Failed to load collection products', err)
            } finally {
                if (activeRequestRef.current === requestId) {
                    setLoadingCollectionId(null)
                }
            }
        },
        [landingPage.landingPageHash, loadingCollectionId, openCollectionGrid],
    )

    const isHorizontal =
        section.collectionsLayout === ApiProductGridLayout.HORIZONTAL_SCROLL
    const isDisabled = !!loadingCollectionId

    const renderTile = (item: ApiCollectionSectionItem) => (
        <CollectionTile
            key={item.hash}
            item={item}
            isHorizontal={isHorizontal}
            isLoading={loadingCollectionId === item.collectionId}
            isDisabled={isDisabled}
            onClick={() => handleTileClick(item)}
        />
    )

    return (
        <SectionContainer>
            {section.sectionTitle && (
                <SectionTitle isHorizontalScroll={isHorizontal}>
                    {section.sectionTitle}
                </SectionTitle>
            )}
            {isHorizontal ? (
                <ProductScrollContainer>
                    {(section.items ?? []).map((item) => (
                        <ProductScrollItem key={item.hash}>
                            {renderTile(item)}
                        </ProductScrollItem>
                    ))}
                </ProductScrollContainer>
            ) : (
                <CollectionsGridContainer>
                    {(section.items ?? []).map(renderTile)}
                </CollectionsGridContainer>
            )}
        </SectionContainer>
    )
}
